import { Toast, updateWidgetContent, Widget, WidgetEditor } from 'jamespot-front-business';
import jApi, {
    CombinedWidgetContent,
    jFileLittle,
    WidgetDisplayMode,
    WidgetQuickSurveyContent,
    WidgetQuickSurveyResponse,
    WidgetQuickSurveyResponseUserList,
    WidgetQuickSurveyType,
    WidgetsName,
} from 'jamespot-user-api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { WidgetFooterView } from './components/WidgetFooterView';
import { WidgetInlineInputText } from './components/WidgetInlineInputText';
import { useWidgetRT } from '../hooks';
import { WidgetIsPending } from './components/WidgetIsPending';
import { JRCFileViewer } from 'jamespot-react-components';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { RTProvider } from '../../RTProvider.component';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

const WidgetQuickSurvey = commonComponentsRegistry.getLazyComponent('WidgetQuickSurvey');
const WidgetQuickSurveyEditor = commonComponentsRegistry.getLazyComponent('WidgetQuickSurveyEditor');
const ModalUserList = commonComponentsRegistry.getLazyComponent('ModalUserList');

const WrapperContainer = styled.div<{ withPadding?: boolean }>`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
    ${(props) => props.withPadding && `padding: 0 12px;`}
    padding-top: 16px;
`;

type JRCWidgetQuickSurveyWrapperProps = {
    widget: WidgetQuickSurveyType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
};

export const JRCWidgetQuickSurveyWrapper = ({ widget, inplace, mode }: JRCWidgetQuickSurveyWrapperProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const { uniqid, content: widgetContent } = widget;
    const [response, setResponse] = useState<WidgetQuickSurveyResponse>({
        userResponses: [],
        totalRespondent: 0,
        totalResponse: 0,
        userLoggedResponses: [],
    });
    const [openUsersModal, setOpenUsersModal] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [usersListIndex, setUsersListIndex] = useState<number>();
    const [usersList, setUsersList] = useState<WidgetQuickSurveyResponseUserList>();

    const [abortController] = useState<AbortController>(new AbortController());
    const ref = useRef<AbortController>(abortController);

    const token = useAppSelector(Widget.selectors.selectToken, shallowEqual);

    const uri = useAppSelector((state) => Widget.selectors.selectWidgetObjectUri(state, widget.uniqid), shallowEqual);

    const widgetObjectRights = useAppSelector(
        (state) => Widget.selectors.selectWidgetObjectRights(state, uniqid),
        shallowEqual,
    );

    const allRt = useAppSelector(
        (state) => Widget.selectors.selectAllWidgetRTObject(state, widget.uniqid),
        shallowEqual,
    );

    const onChange = useCallback(
        (uniqid: string, content: WidgetQuickSurveyContent) => {
            dispatch(updateWidgetContent(uniqid, content));
        },
        [dispatch],
    );

    const onChangeWrapper = (content: { uniqid: string; title?: string }) => {
        dispatch(Widget.slice.actions.updateWidgetWrapper({ ...content }));
    };

    const openEditorPanel = (token: string) => {
        dispatch(WidgetEditor.slice.actions.flushAllEditor());
        dispatch(Widget.slice.actions.setToken({ token }));
        dispatch(
            WidgetEditor.slice.actions.registerEditor({
                uniqid: widget.uniqid,
                name: WidgetsName.QuickSurvey,
            }),
        );
    };

    const handleError = useCallback(
        () => dispatch(Toast.actions.error({ label: 'WIDGET_Presence_Error' })),
        [dispatch],
    );

    const handleResponse = useCallback(
        (uniqid: string) => {
            if (mode === 'edit' || !uri) return;
            setIsPending(true);
            jApi.widget
                .quickSurveyGetResponse({ uri, uniqid }, ref.current.signal)
                .then((response) => {
                    setResponse(response.result);
                })
                .catch((err) => {
                    return !err.aborted && handleError;
                })
                .finally(() => setIsPending(false));
        },
        [handleError, uri, mode],
    );

    const handleWidgetGet = useCallback(() => {
        if (!uri) return;
        setIsPending(true);
        jApi.widget
            .getWidget<WidgetQuickSurveyType>({ uri, uniqid }, ref.current.signal)
            .then((response) => {
                onChange(uniqid, response.result.content);
            })
            .catch((err) => {
                return !err.aborted && handleError;
            })
            .finally(() => setIsPending(false));
    }, [uniqid, handleError, uri, onChange]);

    const handleRespond = (index: number) => {
        if (mode === 'edit' || !uri) return;
        setIsPending(true);
        jApi.widget
            .quickSurveyRespond({ uri, index, multi: widgetContent.multi ? 1 : 0, uniqid }, ref.current.signal)
            .then(() => {
                handleResponse(widget.uniqid);
            })
            .catch((err) => !err.aborted && handleError)
            .finally(() => setIsPending(false));
    };

    const handleCloseSurvey = () => {
        if (!uri) return;
        if (widgetContent.closing) {
            const finalContent = { ...widgetContent, isClosed: true } as CombinedWidgetContent;

            jApi.widget
                .updateWidget<WidgetQuickSurveyType>({
                    uri,
                    uniqid,
                    content: finalContent,
                })
                .catch(() => {
                    handleError();
                });
        }
    };

    const handleProgressBarClick = (index: number) => {
        handleRespond(index);
    };

    const handleAddQuickAnswer = (title: string) => {
        if (!uri) return;

        const newContent = {
            ...widgetContent,
            arr: [...(widgetContent.arr ?? []), { title }],
        } as CombinedWidgetContent;

        jApi.widget
            .quickSurveyAddEnty({ uri, uniqid, label: title })
            .then(() => {
                dispatch(Widget.slice.actions.updateWidget({ uniqid, content: newContent }));
            })
            .catch(handleError);
    };

    const handleLoadUsersList = (index: number, page?: number) => {
        if (!uri) return;
        jApi.widget
            .quickSurveyGetResponseUserList({ uri, uniqid, index, page, limit: 8 }, ref.current.signal)
            .then((response) => {
                setUsersList({
                    ...response.result,
                    data: [...response.result.data],
                    cnt: response.result.cnt,
                });
            })
            .catch((err) => {
                return !err.aborted && handleError;
            });
    };

    const canUpdate = useMemo(() => widgetObjectRights && widgetObjectRights.update, [widgetObjectRights]);

    useEffect(() => {
        handleResponse(widget.uniqid);
    }, [handleResponse, widget.uniqid]);

    useEffect(() => {
        if (!Array.isArray(allRt)) return;
        const lastRt = allRt.at(-1);
        if (!lastRt) return;
        if (!uri) return;

        if (lastRt.object.uri === uri && lastRt.uniqid === widget.uniqid) {
            if (lastRt.fn === 'widget-quick-survey-response') {
                handleResponse(widget.uniqid);
            }
            if (lastRt.fn === 'widget-update') {
                handleWidgetGet();
            }
        }
    }, [allRt, uri, widget.uniqid, handleWidgetGet, handleResponse]);

    const files = useMemo(() => {
        if (!Array.isArray(widgetContent.arr)) return [];

        const fs: jFileLittle[] = [];
        widgetContent.arr.forEach((el) => {
            if (el.file) {
                fs.push(el.file);
            }
        });

        return fs;
    }, [widgetContent]);

    useEffect(() => {
        if (!token && mode === 'edit') {
            jApi.network.token().then((response) => {
                dispatch(Widget.slice.actions.setToken({ token: response.result }));
            });
        }
    }, [mode, token, dispatch]);

    if (mode === 'edit') {
        if (inplace) {
            return (
                <WrapperContainer withPadding={true}>
                    <WidgetQuickSurveyEditor
                        token={token}
                        uniqid={uniqid}
                        content={widget.content}
                        onChangeContent={onChange}
                        onChangeWrapper={onChangeWrapper}
                        handleChange={(content) => onChange(uniqid, content)}
                        inplace={inplace}
                    />
                </WrapperContainer>
            );
        }
    }

    return (
        <RTProvider
            useRT={() => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                useWidgetRT(widget.uniqid);
            }}>
            <WrapperContainer>
                <JRCFileViewer files={files} openWithOptions={[]}>
                    {(open) => {
                        return (
                            <WidgetQuickSurvey
                                limit={response.totalResponse ?? 0}
                                values={widgetContent.arr ?? []}
                                userResponses={response.userResponses}
                                userHasAlreadyVoted={response.userLoggedResponses.length > 0}
                                canCloseSurvey={widget.content.closing ?? false}
                                isClosed={widget.content.isClosed ?? false}
                                open={widget.content.open ?? false}
                                anonymize={widget.content.anonymize ?? false}
                                seeResponseAfter={widget.content.seeResponseAfter ?? false}
                                canUpdate={canUpdate}
                                handleClickFile={(entry) => entry.id && open(entry.id)}
                                handleCloseSurvey={handleCloseSurvey}
                                handleProgressBarClick={handleProgressBarClick}
                                setOpenUsersModal={(index) => {
                                    setUsersListIndex(index);
                                    handleLoadUsersList(index);
                                    setOpenUsersModal(true);
                                }}
                            />
                        );
                    }}
                </JRCFileViewer>

                {widgetContent.open && (
                    <WidgetInlineInputText
                        handleClick={handleAddQuickAnswer}
                        placeholder={intl.formatMessage({ id: 'FIELDS_Add_Option' })}
                    />
                )}
                {canUpdate && (
                    <WidgetFooterView
                        uniqid={widget.uniqid}
                        name={widget.name}
                        collaborationRights={{
                            update: !!canUpdate,
                        }}
                        mode={mode}
                        handleOpenEditor={() => {
                            if (!token) {
                                setIsPending(true);
                                jApi.network
                                    .token()
                                    .then((response) => {
                                        dispatch(Widget.slice.actions.setToken({ token: response.result }));
                                        openEditorPanel(response.result);
                                    })
                                    .finally(() => setIsPending(false));
                            } else {
                                openEditorPanel(token);
                            }
                        }}
                    />
                )}
                {isPending && <WidgetIsPending />}
                <ModalUserList
                    open={openUsersModal}
                    usersList={usersList}
                    title={widgetContent.arr?.[usersListIndex ?? 0]?.title ?? ''}
                    setOpen={setOpenUsersModal}
                    handleLoadUsersList={(page) => {
                        handleLoadUsersList(usersListIndex ?? 0, page);
                    }}
                    onClose={() => {
                        setUsersList({
                            limit: 0,
                            page: 0,
                            data: [],
                            cnt: 0,
                        });
                    }}
                />
            </WrapperContainer>
        </RTProvider>
    );
};
